import { AnalyticsStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject, observer } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { ClientLogger } from 'bernie-client';
import { ErrorBoundary } from 'bernie-view';
import { merge } from 'bernie-core';
import { performanceMetrics } from 'bernie-analytics';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { DateType } from 'constants/dates';

import { UitkCard } from 'uitk-react-cards';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { DateStepper } from 'components/infosite/rooms-and-rates/availability-wizard/internals/date-stepper';
import { SearchValidation } from 'components/infosite/rooms-and-rates/availability-wizard/internals/search-validation';

const logger = ClientLogger.getLoggerWithIdentifier('availabilityWizard.error');

const { withKeys } = merge;

export interface LocalizedAvailabilityWizardProps extends LocalizedComponentProps, AvailabilityWizardProps {
  experiment?: ExperimentStore;
}

export interface AvailabilityWizardProps extends Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  currentHotelOnly?: boolean;
  onUpdateResults: (event?: Event, searchCriteriaModel?: any, goBack?: boolean) => void;
}

export interface AvailabilityWizardState {
  inputErrors: AvailabilityWizardInputErrors;
  midnightBookingOffsetHours: number;
  numDaysBookableInAdvance: number;
}

export interface AvailabilityWizardInputErrors {
  adults?: string;
  children?: string;
  endDate?: string;
  startDate?: string;
}

// @ts-ignore
@withRouter
@withLocalization
@inject('currentHotel', 'analytics', 'experiment')
@observer
export class AvailabilityWizard extends React.Component<LocalizedAvailabilityWizardProps, AvailabilityWizardState> {
  private searchValidator: SearchValidation;

  constructor(props: any) {
    super(props);
    const { l10n } = props;

    this.searchValidator = new SearchValidation(l10n.formatText, props.experiment);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.state = { inputErrors: {}, midnightBookingOffsetHours: 4, numDaysBookableInAdvance: 500 };
  }

  private handleChange(event: Event, isTriggeredByStepper?: boolean) {
    let target: any = event.target;

    if (!Array.isArray(target)) {
      target = [target];
    }

    const targetDictionary: Record<string, any> = {};
    target.forEach((searchChangeProp: { name: string; value: any }) => {
      targetDictionary[searchChangeProp.name] = searchChangeProp.value;
    });

    const offerSearchData = withKeys(CurrentHotelStore.offerSearchKeys).merge({}, targetDictionary);

    this.props.currentHotel?.setOfferData(offerSearchData);
    this.handleSubmit(isTriggeredByStepper);
  }

  private logTracking(rfrr: string, linkName: string) {
    const { analytics } = this.props;
    if (analytics && typeof analytics.clickTracker === 'function') {
      this.props.analytics?.clickTracker(rfrr, linkName, null, null)();
    }
  }

  private handleSubmit(isTriggeredByStepper = false) {
    const { currentHotel, history } = this.props;
    const errorState = {
      inputErrors: {},
    };

    const startDateError = currentHotel && this.searchValidator.startDate(currentHotel.offerSearchData);
    const endDateError = currentHotel && this.searchValidator.endDate(currentHotel.offerSearchData);
    const adultError = currentHotel && this.searchValidator.adults(currentHotel.offerSearchData);
    const childrenError = currentHotel && this.searchValidator.children(currentHotel.offerSearchData);

    if (startDateError || endDateError || adultError || childrenError) {
      errorState.inputErrors = {
        startDate: startDateError,
        endDate: endDateError,
        adults: adultError,
        children: childrenError,
      };
    }

    if (this.hasInputErrors(errorState)) {
      if (!isTriggeredByStepper && history) {
        history.goBack();
      }
      const errorMessages: any[] = [];
      const errors: any = errorState.inputErrors;
      for (const i in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, i) && errors[i] !== undefined) {
          errorMessages.push(errors[i]);
        }
      }
      if (errorMessages.length > 0) {
        logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_HANDLE_SUBMIT'), JSON.stringify(errorMessages));
      }
    } else {
      performanceMetrics.reset();
      const { onUpdateResults } = this.props;
      if (onUpdateResults && typeof onUpdateResults === 'function') {
        if (isTriggeredByStepper) {
          onUpdateResults(undefined, currentHotel?.offerSearchData, false);
          this.logTracking('ChangeDatesStepper:Submit', 'Submitsearch using date stepper buttons');
        } else {
          onUpdateResults(undefined, currentHotel?.offerSearchData, true);
          this.logTracking('FORM.SUBMIT', 'Submit search form');
        }
      }
    }
    this.setState(errorState);
  }

  private hasInputErrors(validationItems: { inputErrors: any }) {
    const temp = validationItems && validationItems;
    for (const key in temp.inputErrors) {
      if (temp.inputErrors[key] !== undefined) {
        return true;
      }
    }
  }

  public render() {
    const { currentHotel, l10n } = this.props;
    if (!currentHotel) return null;

    const { inputErrors, midnightBookingOffsetHours, numDaysBookableInAdvance } = this.state;
    const { formatText } = l10n;
    const { offerSearchData } = currentHotel;

    const startDate = offerSearchData.startDate ?? '';
    const endDate = offerSearchData.endDate ?? '';

    const stepperProps = {
      clickHandler: this.handleChange,
      endDate,
      startDate,
      invalid: inputErrors && inputErrors.startDate,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
    };

    return (
      <UitkSpacing padding={{ block: 'three' }}>
        <div>
          <ErrorBoundary label="hotel.information.rooms.availabilityWizardStepper">
            <UitkCard>
              <UitkText align="center">
                <UitkLayoutFlex direction="row" justifyContent="space-between">
                  <UitkLayoutFlexItem minWidth="half_width">
                    <UitkLayoutFlex direction="column" justifyContent="space-between">
                      <DateStepper
                        buttonText={formatText('hotels.infosite.searchForm.availabilityButton.label')}
                        {...stepperProps}
                        dateType={DateType.CHECK_IN}
                      />
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                  <UitkLayoutFlexItem minWidth="half_width">
                    <UitkLayoutFlex direction="column" justifyContent="space-between">
                      <DateStepper
                        buttonText={formatText('hotels.infosite.searchForm.availabilityButton.label')}
                        {...stepperProps}
                        dateType={DateType.CHECK_OUT}
                      />
                    </UitkLayoutFlex>
                  </UitkLayoutFlexItem>
                </UitkLayoutFlex>
              </UitkText>
            </UitkCard>
          </ErrorBoundary>
        </div>
      </UitkSpacing>
    );
  }
}
